import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConversation } from '@elevenlabs/react';
import type { InterviewPreview, InterviewSession, InterviewStatus } from '@ai-interview/shared';
import { api } from '../lib/api';
import { ErrorNotice } from '../components/Status';
import { AgentVoiceVisualizer } from '../components/AgentVoiceVisualizer';

type Phase = 'loading' | 'ready' | 'connecting' | 'live' | 'ended' | 'error';

interface Turn {
  who: 'Interviewer' | 'You';
  text: string;
}

/** What the page says when a link opens on an interview that cannot be started. */
const UNAVAILABLE: Partial<Record<InterviewStatus, string>> = {
  in_progress: 'This interview was already started. Ask your recruiter for a new link.',
  expired: 'This interview link has expired. Ask your recruiter for a new one.',
  abandoned: 'This interview was started but not finished. Ask your recruiter for a new link.',
  revoked: 'This interview link has been withdrawn. Contact your recruiter if you think that is a mistake.',
};

/**
 * Proves a microphone can be opened, then lets it go. The SDK opens its own
 * stream once the session starts; releasing this one straight away keeps the
 * recording indicator from staying lit and leaves the device free.
 */
async function checkMicrophone(): Promise<void> {
  if (typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    throw new Error(
      'This browser cannot use a microphone on this page. Open the link in a recent version of Chrome, Edge, Safari or Firefox.',
    );
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  for (const track of stream.getTracks()) track.stop();
}

/**
 * Says what to do about a microphone that could not be opened. Nothing has been
 * spent at this point, so every message can honestly say "press Start again".
 */
function microphoneProblem(err: unknown): unknown {
  if (!(err instanceof DOMException)) return err;
  switch (err.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new Error(
        'Microphone access is blocked. Allow it for this site in your browser settings, then press Start again - your interview has not started.',
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new Error(
        'No microphone was found. Connect one (headphones with a mic work well), then press Start again.',
      );
    case 'NotReadableError':
    case 'AbortError':
      return new Error(
        'Your microphone could not be opened - another app may be using it. Close that app, then press Start again.',
      );
    default:
      return err;
  }
}

/**
 * The candidate's interview room.
 *
 * Two server calls, and the split matters. `previewInterview` is a read: it
 * describes the role and the candidate so the page can render, and costs
 * nothing, so opening or refreshing the link is free. `startSession` is what
 * spends the single attempt, and only Start calls it.
 *
 * The page never sees the ElevenLabs API key, and never carries the interview
 * prompt: the server bakes that into a per-interview agent and hands back only
 * an id and a short-lived token, so a candidate cannot read the questions ahead
 * of time or rewrite the interviewer's instructions from the console.
 */
export function CandidateInterviewPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState<Phase>('loading');
  // Two stages on purpose. `preview` is a read that costs nothing, so opening
  // or refreshing the link is free; `session` only exists once the candidate
  // has actually pressed Start and spent their single attempt.
  const [preview, setPreview] = useState<InterviewPreview>();
  const [session, setSession] = useState<InterviewSession>();
  const [error, setError] = useState<unknown>();
  const [turns, setTurns] = useState<Turn[]>([]);
  const transcriptRef = useRef<HTMLDivElement>(null);

  const conversation = useConversation({
    onConnect: () => setPhase('live'),
    onDisconnect: () => setPhase((current) => (current === 'live' ? 'ended' : current)),
    onMessage: (message: { message?: string; source?: string }) => {
      if (!message.message) return;
      setTurns((current) => [
        ...current,
        { who: message.source === 'user' ? 'You' : 'Interviewer', text: message.message! },
      ]);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err : new Error(String(err)));
      setPhase('error');
    },
  });

  useEffect(() => {
    if (!token) {
      setError(new Error('This link is missing its interview token. Ask your recruiter to resend it.'));
      setPhase('error');
      return;
    }
    api
      .previewInterview(token)
      .then((result) => {
        setPreview(result);
        if (result.status === 'invited') {
          setPhase('ready');
          return;
        }
        // Say plainly what happened instead of letting Start fail with a 409.
        setError(new Error(UNAVAILABLE[result.status] ?? 'This interview has already been completed.'));
        setPhase('error');
      })
      .catch((err: unknown) => {
        setError(err);
        setPhase('error');
      });
  }, [token]);

  // Keep the newest turn in view without yanking the page around.
  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  const start = useCallback(async () => {
    if (!token) return;
    setPhase('connecting');
    setError(undefined);

    // The microphone comes first, while nothing has been spent. Starting the
    // session marks the invite used, so asking afterwards meant a candidate who
    // blocked the prompt was told to try again - and then turned away by the
    // retry, because their one attempt was already gone.
    try {
      await checkMicrophone();
    } catch (err) {
      setError(microphoneProblem(err));
      setPhase('ready');
      return;
    }

    try {
      // The attempt is spent here, not on page load.
      const session = await api.startSession(token);
      setSession(session);

      await conversation.startSession({
        conversationToken: session.conversationToken,
        connectionType: 'webrtc',
        dynamicVariables: {
          // The one that matters: the server resolves this into the actual
          // prompt, the questions and the boundaries. Nothing else here shapes
          // the interview.
          interview_token: session.interviewToken,

          // How the post-call webhook finds its way home. ElevenLabs echoes
          // these back on the transcript payload, and post-call.ts resolves the
          // interview from `interview_id` first.
          //
          // This used to be set by the conversation-initiation webhook, which
          // also wrote the CONV#<id> pointer as a fallback. That webhook does
          // not run for SDK conversations at all - only for inbound telephony
          // and messaging - so on a browser interview neither path existed and
          // every completed call logged "No interview matches this
          // conversation" and sat at `in_progress` for ever, transcript
          // discarded. The id is already in the session payload the browser
          // holds, so sending it exposes nothing new.
          interview_id: session.interviewId,

          // ElevenLabs renders the agent's stored `first_message` template as
          // the session opens, and rejects the conversation outright if any
          // placeholder in it is unsupplied - before it calls the
          // conversation-initiation webhook, so the server never gets a chance
          // to fill them in. The failure is a 1008 close naming the missing
          // variables and no webhook request at all.
          //
          // These three are safe to send from the browser: the page already
          // displays all of them, so the candidate learns nothing new. The
          // init webhook still overrides the first message and the prompt with
          // its own authoritative copy once the conversation is up.
          candidate_name: session.candidateName,
          company_name: session.companyName,
          job_title: session.jobTitle,
        },
      });
    } catch (err) {
      setError(err);
      setPhase('error');
    }
  }, [conversation, token]);

  const end = useCallback(async () => {
    await conversation.endSession();
    setPhase('ended');
  }, [conversation]);

  // Read live rather than captured: the SDK swaps these out when the session
  // starts, and a stale closure would meter a conversation that has ended.
  const agentFrequencies = useCallback(
    () => conversation.getOutputByteFrequencyData(),
    [conversation],
  );
  const candidateFrequencies = useCallback(
    () => conversation.getInputByteFrequencyData(),
    [conversation],
  );
  const agentLevel = useCallback(() => conversation.getOutputVolume(), [conversation]);
  const candidateLevel = useCallback(() => conversation.getInputVolume(), [conversation]);

  // What the ring says outside a call. During one, the visualiser supplies its
  // own Speaking / Listening labels (undefined); null hides the label where the
  // page's own text already explains the moment.
  const stageStatus =
    phase === 'loading'
      ? { title: 'Preparing', detail: 'Getting your interview ready' }
      : phase === 'connecting'
        ? { title: 'Connecting', detail: 'Setting up the interviewer and your microphone' }
        : phase === 'live'
          ? undefined
          : null;

  return (
    <div className="interview-scene">
      {/* One scene for the whole visit - from the moment the link opens to the
          thank-you - so the ring reads as part of the page, not a video in a
          frame, and the candidate never sees the app's light theme. */}
      <div className="interview-scene-backdrop" aria-hidden="true">
        <div className="interview-scene-blobs">
          <span className="interview-scene-blob a" />
          <span className="interview-scene-blob b" />
          <span className="interview-scene-blob c" />
        </div>
        <div className="interview-scene-grid" />
      </div>

      <main className="interview-content">
        {preview ? (
          <header className="interview-heading">
            <h1>{preview.jobTitle}</h1>
            <p className="muted">
              {preview.companyName} · about {preview.durationMinutes} minutes
            </p>
          </header>
        ) : null}

        <AgentVoiceVisualizer
          active={phase === 'live'}
          status={stageStatus}
          agentSpeaking={conversation.isSpeaking}
          getAgentFrequencies={agentFrequencies}
          getCandidateFrequencies={candidateFrequencies}
          getAgentLevel={agentLevel}
          getCandidateLevel={candidateLevel}
        />

        <ErrorNotice error={error} />

        {phase === 'ready' && preview ? (
          <div className="interview-intro">
            <p>
              Hi {preview.candidateName}. You are about to speak with an AI interviewer. It will ask
              you a few questions about your experience, and you can answer out loud just as you
              would with a person.
            </p>
            <p className="small muted">
              The conversation is recorded and transcribed so the hiring team can review it. Find a
              quiet spot, use headphones if you have them, and allow microphone access when your
              browser asks.
            </p>
            <button className="scene-button primary" onClick={() => void start()}>
              Start the interview
            </button>
          </div>
        ) : null}

        {phase === 'live' ? (
          <button className="scene-button danger" onClick={() => void end()}>
            End interview
          </button>
        ) : null}

        {phase === 'ended' ? (
          <div className="interview-intro">
            <h2>That is everything — thank you.</h2>
            <p className="muted">
              Your conversation has been sent to the hiring team. They will follow up by email. You
              can close this tab now.
            </p>
          </div>
        ) : null}

        {turns.length > 0 ? (
          <div className="transcript" ref={transcriptRef}>
            {turns.map((turn, index) => (
              <div key={index} className="turn">
                <div className="who">{turn.who}</div>
                <div>{turn.text}</div>
              </div>
            ))}
          </div>
        ) : null}
      </main>
    </div>
  );
}
